import { useCallback, useEffect, useRef, useState } from "react";

import type { AgentScreen, HandoffChatEntry, HandoffResolution } from "@ark-bot/protocol";

import { api, type VncConfig } from "../api.js";
import { configuredHost, socketUrl } from "../host.js";
import { useText } from "../i18n.js";
import type { AgentCursor } from "../state.js";
import { COMPUTER } from "../text/computer.js";

/**
 * noVNC's client, loaded when a screen is first shown and not with the page:
 * the library probes the browser's video decoder as it loads, and where the
 * probe never answers (dev/novnc-probe.ts) that must cost the screen, never
 * the app.
 */
type Rfb = import("@novnc/novnc").default;
const loadRfb = (): Promise<typeof import("@novnc/novnc").default> => import("@novnc/novnc").then((module) => module.default);

interface Props {
  readonly agentName: string;
  /** The agent is acting on the desktop right now. */
  readonly inUse: boolean;
  /** Where the agent last pointed, for the overlay. */
  readonly cursor: AgentCursor | null;
  /** The screens this agent has, in display order; the pane shows these and no others (§6.15). */
  readonly screens: readonly AgentScreen[];
  /** The agent's open request for the box, if it made one. */
  readonly handoff: HandoffChatEntry | null;
  readonly onResolveHandoff: (resolution: HandoffResolution) => Promise<void>;
  readonly onClose: () => void;
  /** `card`: the picture alone, small, in the details pane; `pane`: the full thing beside the chat, with take-over. */
  readonly variant?: "pane" | "card";
  /** A card's "Open": show the full pane. */
  readonly onExpand?: () => void;
}

type Phase = "loading" | "idle" | "connecting" | "connected" | "unavailable";

/** Why the screen is out of reach, worded when drawn so it follows the app's language; `error` is text from elsewhere, shown as it came. */
type Detail =
  | { readonly kind: "not-served" | "closed" | "dropped" }
  | { readonly kind: "refused"; readonly reason: string | null }
  | { readonly kind: "viewer"; readonly error: string }
  | { readonly kind: "error"; readonly message: string };

function detailText(detail: Detail, t: (typeof COMPUTER)["en"]): string {
  switch (detail.kind) {
    case "not-served":
      return t.noScreenServed;
    case "closed":
      return t.closed;
    case "dropped":
      return t.dropped;
    case "refused":
      return t.refused(detail.reason ?? t.unknownReason);
    case "viewer":
      return t.viewerFailed(detail.error);
    case "error":
      return detail.message;
  }
}

/**
 * The live screen, as the prototype shows "Grok Bot's Computer" beside the
 * chat: noVNC's client on the box's display through the host's relay, view
 * only until the user takes over, with the agent's pointer drawn over the
 * picture while it works. Fullscreen is the pane itself.
 */
export function Computer({ agentName, inUse, cursor, screens, handoff, onResolveHandoff, onClose, variant = "pane", onExpand }: Props): React.JSX.Element {
  const t = useText(COMPUTER);
  const pane = useRef<HTMLElement>(null);
  const screen = useRef<HTMLDivElement>(null);
  const rfb = useRef<Rfb | null>(null);
  const [config, setConfig] = useState<VncConfig | null>(null);
  /** Which of this agent's screens is on show, by display; a screen it loses simply falls out of the strip (§6.15). */
  const [chosen, setChosen] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [detail, setDetail] = useState<Detail | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [interactive, setInteractive] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    api
      .vnc()
      .then((loaded) => {
        if (cancelled) return;
        // The host serves no screen. App keeps this pane off a host like that,
        // so reaching here means one stopped serving one under us.
        if (loaded == null) {
          setPhase("unavailable");
          setDetail({ kind: "not-served" });
          return;
        }
        setConfig(loaded);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setPhase("unavailable");
        setDetail({ kind: "error", message: error instanceof Error ? error.message : String(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  /**
   * This agent's screens, each joined to where the box serves it. The catalogue
   * is every screen the box has; the agent's list is the ones that are its own,
   * so nobody is shown another agent's desktop - the pane is one agent's
   * computer and its subagents' monitors, as the prototype's is.
   */
  const panes = screens.flatMap((screen) => {
    const served = config?.screens.find((one) => one.display === screen.display);
    return served == null ? [] : [{ ...screen, ...served }];
  });
  const handoffPane = handoff?.status === "waiting" ? panes.find((pane) => pane.display === handoff.display) : undefined;
  const current = handoffPane ?? panes.find((pane) => pane.display === chosen) ?? panes[0] ?? null;
  const busyDisplay = panes.find((pane) => pane.busy)?.display ?? null;
  const displays = panes.map((pane) => pane.display).join(",");

  useEffect(() => {
    const target = screen.current;
    // Nothing of this agent's to show: an idle pane, never a look at whatever
    // another agent happens to be doing on the box's other screen.
    if (config != null && current == null) {
      setPhase("idle");
      setDetail(null);
      return;
    }
    if (current == null || config == null || target == null) return;
    setPhase("connecting");
    setDetail(null);
    // A client we have moved on from still fires its disconnect; without this
    // the switch to another screen (or to none) lands as "can't reach it".
    let live = true;
    let client: Rfb | null = null;
    // On the host that serves the page, or on the one the packaged app was given (client-app §5.3).
    const socket = socketUrl(configuredHost(), window.location, current.socketPath);
    const password = config.password;
    loadRfb().then(
      (RFB) => {
        if (!live) return;
        client = new RFB(target, socket, { shared: true, credentials: { password }, wsProtocols: ["binary"] });
        client.viewOnly = true;
        client.focusOnClick = false;
        client.scaleViewport = true;
        client.resizeSession = false;
        client.showDotCursor = false;
        client.background = "transparent";
        client.addEventListener("connect", () => {
          if (live) setPhase("connected");
        });
        client.addEventListener("credentialsrequired", () => client?.sendCredentials({ password }));
        client.addEventListener("securityfailure", (event) => {
          if (!live) return;
          const reason = (event as CustomEvent<{ reason?: string }>).detail?.reason;
          setDetail({ kind: "refused", reason: reason == null ? null : String(reason) });
        });
        client.addEventListener("disconnect", (event) => {
          if (!live) return;
          const clean = (event as CustomEvent<{ clean?: boolean }>).detail?.clean === true;
          setPhase("unavailable");
          setDetail((current) => current ?? { kind: clean ? "closed" : "dropped" });
        });
        rfb.current = client;
      },
      (error: unknown) => {
        if (!live) return;
        setPhase("unavailable");
        setDetail({ kind: "viewer", error: error instanceof Error ? error.message : String(error) });
      },
    );
    return () => {
      live = false;
      rfb.current = null;
      client?.disconnect();
    };
  }, [config, current?.socketPath]);

  // A card only ever watches; taking over is the pane's.
  const driving = variant === "pane" && interactive;

  useEffect(() => {
    const client = rfb.current;
    if (client == null) return;
    client.viewOnly = !driving;
    client.focusOnClick = driving;
    if (driving) client.focus();
    else client.blur();
  }, [driving, phase]);

  // Being asked for the box means driving it: the screen goes live for the user
  // while the request stands, and goes back to watching once they answer, so
  // the agent and the user are never both on the mouse.
  useEffect(() => {
    setInteractive(handoff != null);
  }, [handoff?.id]);

  useEffect(() => {
    const onChange = (): void => setFullscreen(document.fullscreenElement === pane.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = useCallback((): void => {
    if (document.fullscreenElement === pane.current) void document.exitFullscreen();
    else void pane.current?.requestFullscreen();
  }, []);

  /**
   * Arrow keys step between screens, as the prototype's strip does — but only
   * while the user is watching: once they have taken over, every key belongs to
   * the computer.
   */
  useEffect(() => {
    const order = displays.split(",").filter((display) => display.length > 0);
    if (driving || variant !== "pane" || order.length < 2) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      const target = event.target as HTMLElement | null;
      if (target != null && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      event.preventDefault();
      setChosen((display) => {
        const index = Math.max(0, order.indexOf(display ?? order[0]!));
        return order[(index + (event.key === "ArrowLeft" ? order.length - 1 : 1)) % order.length] ?? display;
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [driving, variant, displays]);

  // Escape puts the screen away, unless the user is driving it: then every key is the desktop's, Escape too.
  useEffect(() => {
    if (driving || variant !== "pane") return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [driving, variant, onClose]);

  // The agent is asking for something on its screen, so pull the strip to it.
  useEffect(() => {
    if (handoff == null || busyDisplay == null) return;
    setChosen(handoff.display ?? busyDisplay);
  }, [handoff?.id, handoff?.display, busyDisplay]);

  const retry = (): void => {
    setConfig(null);
    setAttempt((count) => count + 1);
  };

  const width = current?.width ?? 1280;
  const height = current?.height ?? 800;
  const message =
    phase === "idle"
      ? t.noScreenYet(agentName)
      : phase === "loading" || phase === "connecting"
        ? t.booting
        : phase === "unavailable"
          ? t.cantReach(agentName)
          : null;
  // A screen is the agent's from the first time it uses the desktop; until then there is nothing of its own to watch.
  const note = phase === "idle" ? t.idleNote : detail == null ? null : detailText(detail, t);

  const stage = (
    <div className="computer-stage">
      <div className="computer-screen" ref={screen} style={{ aspectRatio: `${width} / ${height}` }} data-phase={phase}>
        {phase === "connected" && !driving && cursor != null ? (
          <div key={cursor.seq} className={`computer-cursor computer-cursor-${cursor.type}`} style={{ left: `${(cursor.x / width) * 100}%`, top: `${(cursor.y / height) * 100}%` }} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path d="M4 3 L20 11.5 L13 13.5 L10.5 20.5 Z" fill="#fff" stroke="#000" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
          </div>
        ) : null}
      </div>
      {message != null ? (
        <div className="computer-message" role="status">
          <span>{message}</span>
          {note != null ? <span className="muted">{note}</span> : null}
          {phase === "unavailable" ? (
            <button type="button" onClick={retry}>
              {t.retry}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  // The card: the picture alone, as the prototype's thumbnail under the chat
  // header, with "Open" for the full pane. The button lies over the whole
  // picture: noVNC's canvas swallows every click it gets, so the card itself
  // never hears one once the screen is live.
  if (variant === "card") {
    return (
      <div className={`screen-card${phase === "connected" ? " live" : ""}${inUse ? " in-use" : ""}${message == null ? "" : " has-message"}`} aria-label={t.screenOf(agentName)}>
        {stage}
        {onExpand != null ? (
          <button type="button" className="screen-cover" onClick={onExpand} title={t.openLarge}>
            <span className="screen-open">
              <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
                <path d="M11.5 3.5H16.5V8.5M16.5 3.5 11 9M8.5 16.5H3.5V11.5M3.5 16.5 9 11" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t.open}
            </span>
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <aside className={`computer-pane${driving ? " interactive" : ""}`} aria-label={t.computerOf(agentName)} ref={pane}>
      <div className="computer-head">
        <span className="computer-title">
          {t.computerOf(agentName)}{inUse ? <span className="computer-in-use">{t.inUse}</span> : null}
        </span>
        <div className="computer-actions">
          <button type="button" onClick={() => setInteractive((current) => !current)} disabled={phase !== "connected"} aria-pressed={driving} title={driving ? t.stopDriving : t.startDriving}>
            {driving ? t.handBack : t.takeOver}
          </button>
          <button type="button" onClick={toggleFullscreen} aria-pressed={fullscreen} title={fullscreen ? t.exitFullscreen : t.fullscreen}>
            {fullscreen ? t.exitFullscreen : t.fullscreen}
          </button>
          <button type="button" className="icon" onClick={onClose} aria-label={t.close} title={t.close}>
            ×
          </button>
        </div>
      </div>
      {handoff != null ? (
        <div className="computer-attention" role="group" aria-label={t.needsAttention}>
          <div className="computer-attention-text">
            <strong>{t.needsAttention}</strong>
            <span>{handoff.instruction}</span>
          </div>
          <div className="computer-attention-actions">
            <button type="button" onClick={() => void onResolveHandoff("skipped")} title={t.skipStepTitle}>
              {t.skipStep}
            </button>
            <button type="button" className="primary" onClick={() => void onResolveHandoff("handed_back")}>
              {t.doneContinue}
            </button>
          </div>
        </div>
      ) : null}
      {panes.length > 1 ? (
        <div className="screen-strip" role="tablist" aria-label={t.screens}>
          {panes.map((one) => {
            const showing = one.display === current?.display;
            return (
              <button
                key={one.display}
                type="button"
                role="tab"
                aria-selected={showing}
                className={showing ? "screen-tab chosen" : "screen-tab"}
                onClick={() => setChosen(one.display)}
                title={one.busy ? t.screenWorking(one.title, handoff != null) : t.screenIdle(one.title)}
              >
                {one.title}
                {one.busy ? <span className={handoff == null ? "screen-dot busy" : "screen-dot attention"} aria-hidden="true" /> : null}
              </button>
            );
          })}
          <span className="muted screen-hint">{t.stepHint}</span>
        </div>
      ) : null}
      {stage}
      <div className="computer-foot muted">
        {handoff != null
          ? t.footWaiting(agentName)
          : driving
            ? t.footDriving
            : inUse
              ? t.footWorking(agentName)
              : t.footWatching}
      </div>
    </aside>
  );
}
